import React from 'react';
import { useStudioTypeSize } from '../../StudioTypeScale';
import { resolveComparisonContent } from '../../../../utils/contentCopy';
import SpotlightFrame, { type SpotlightInjectedProps, type SpotlightLayoutProps, type SpotlightTokens } from './SpotlightFrame';

const SpotlightColumn: React.FC<{
  title: string;
  items: string[];
  positive: boolean;
  isStory?: boolean;
  tokens: SpotlightTokens;
}> = ({ title, items, positive, isStory, tokens: t }) => {
  const color = positive ? t.positive : t.negative;
  const typeSize = useStudioTypeSize();

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        border: `1px solid ${color}`,
        background: t.panel
      }}
    >
      <div
        style={{
          padding: isStory ? '10px 9px' : '8px 7px',
          color: positive ? '#06120d' : '#17070a',
          background: color,
          fontSize: typeSize(isStory ? 11 : 9),
          fontWeight: 900,
          letterSpacing: 0.9,
          textTransform: 'uppercase'
        }}
      >
        {positive ? '✓ ' : '× '}
        {title}
      </div>
      <div style={{ padding: isStory ? '5px 10px 8px' : '4px 8px 6px' }}>
        {items.map((item, index) => (
          <div
            key={`${index}-${item}`}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 7,
              padding: isStory ? '9px 0' : '7px 0',
              borderBottom: index === items.length - 1 ? 'none' : `1px solid ${t.line}`,
              color: t.ink,
              fontSize: typeSize(isStory ? 11.5 : 9.8),
              fontWeight: 650,
              lineHeight: 1.4
            }}
          >
            <span style={{ color, fontWeight: 900 }}>{String(index + 1).padStart(2, '0')}</span>
            <span>{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const SpotlightComparisonContent: React.FC<SpotlightInjectedProps & { content: any }> = ({
  content,
  tokens,
  isStory,
  locale
}) => {
  const t = tokens!;
  const typeSize = useStudioTypeSize();
  const resolved = resolveComparisonContent(content, locale);

  return (
    <div>
      <h2
        style={{
          maxWidth: 305,
          margin: isStory ? '0 0 22px' : '0 0 16px',
          color: t.ink,
          fontSize: typeSize(isStory ? 31 : 25),
          fontWeight: 900,
          letterSpacing: '-1px',
          lineHeight: 1.08
        }}
      >
        {resolved.title}
      </h2>
      <div style={{ display: 'flex', gap: isStory ? 10 : 8 }}>
        <SpotlightColumn
          title={resolved.leftTitle}
          items={resolved.leftItems}
          positive={false}
          isStory={isStory}
          tokens={t}
        />
        <SpotlightColumn
          title={resolved.rightTitle}
          items={resolved.rightItems}
          positive
          isStory={isStory}
          tokens={t}
        />
      </div>
    </div>
  );
};

const SpotlightComparisonLayout: React.FC<SpotlightLayoutProps> = props => (
  <SpotlightFrame {...props}>
    <SpotlightComparisonContent content={props.content || {}} />
  </SpotlightFrame>
);

export default SpotlightComparisonLayout;
